import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "vacancy-list-container" }
const _hoisted_2 = {
  key: 0,
  class: "loading-state"
}
const _hoisted_3 = {
  key: 1,
  class: "error-state"
}
const _hoisted_4 = { key: 2 }
const _hoisted_5 = { class: "top-section" }
const _hoisted_6 = { class: "summary-cards" }
const _hoisted_7 = { class: "summary-card" }
const _hoisted_8 = { class: "card-content" }
const _hoisted_9 = { class: "card-number" }
const _hoisted_10 = { class: "summary-card" }
const _hoisted_11 = { class: "card-content" }
const _hoisted_12 = { class: "card-number" }
const _hoisted_13 = { class: "summary-card" }
const _hoisted_14 = { class: "card-content" }
const _hoisted_15 = { class: "card-number" }
const _hoisted_16 = { class: "search-filter-section" }
const _hoisted_17 = { class: "search-box" }
const _hoisted_18 = { class: "filters" }
const _hoisted_19 = { class: "date-filters" }
const _hoisted_20 = ["value"]
const _hoisted_21 = {
  key: 0,
  class: "no-results"
}
const _hoisted_22 = {
  key: 1,
  class: "table-responsive"
}
const _hoisted_23 = { class: "vacancy-table" }
const _hoisted_24 = { class: "pagination" }
const _hoisted_25 = ["disabled"]
const _hoisted_26 = { class: "page-info" }
const _hoisted_27 = ["disabled"]

import { ref, computed, onMounted } from 'vue'
import { format } from 'date-fns'

interface Vacancy {
  _id: string
  siteId: {
    _id: string
    siteName: string
  }
  siteName: string
  departmentId: {
    _id: string
  }
  departmentName: string
  postVacant: string
  relieverId?: {
    _id: string
    empName: string
  }
  relieverName?: string
  changeType: string
  gender: string
  salary: number
  pfEsi: boolean
  stay: boolean
  weeklyOff: string
  food: string
  location: string
  workingHours: number
  operationDeadline: string
  recruitmentDeadline: string
  missedDeadlines: number
  status: string
  todayJoining: number
  isActive: boolean
  vacancyDate: string
  createdAt: string
  updatedAt: string
}

interface ApiResponse {
  success: boolean
  count: number
  data: Vacancy[]
  filters: any
  message: string
}

// Replace the sample data with API call
const itemsPerPage = 10

// Add after searchQuery ref

export default /*@__PURE__*/_defineComponent({
  __name: 'vacancy-list',
  setup(__props) {

const vacancies = ref<Vacancy[]>([])
const isLoading = ref(true)
const error = ref<string | null>(null)

// Fetch vacancies from API
const fetchVacancies = async () => {
  try {
    isLoading.value = true
    const baseUrl = process.env.VUE_APP_BASE_URL
    if (!baseUrl) {
      throw new Error('API base URL not configured')
    }
    
    const response = await fetch(`${baseUrl}api/get/vacancy`)
    const data: ApiResponse = await response.json()
    
    if (data.success) {
      vacancies.value = data.data
    } else {
      error.value = data.message || 'Failed to fetch vacancies'
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to fetch vacancies'
    console.error('Error fetching vacancies:', err)
  } finally {
    isLoading.value = false
  }
}

// Call fetchVacancies when component mounts
onMounted(() => {
  fetchVacancies()
})

// Filter and Search state
const searchQuery = ref('')
const selectedDepartment = ref('')
const selectedStatus = ref('')
const currentPage = ref(1)
const fromDate = ref('')
const toDate = ref('')

// Computed properties
const departments = computed(() => 
  [...new Set(vacancies.value.map(v => v.departmentName))]
)

const filteredVacancies = computed(() => {
  let filtered = [...vacancies.value]

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(v => 
      v.siteName.toLowerCase().includes(query) ||
      v.departmentName.toLowerCase().includes(query) ||
      v.location.toLowerCase().includes(query)
    )
  }

  // Apply date range filter
  if (fromDate.value && toDate.value) {
    const startDate = new Date(fromDate.value)
    const endDate = new Date(toDate.value)
    endDate.setHours(23, 59, 59) // Include the entire end date

    filtered = filtered.filter(v => {
      const vacancyDate = new Date(v.vacancyDate)
      return vacancyDate >= startDate && vacancyDate <= endDate
    })
  }

  // Apply department filter
  if (selectedDepartment.value) {
    filtered = filtered.filter(v => v.departmentName === selectedDepartment.value)
  }

  // Apply status filter
  if (selectedStatus.value) {
    filtered = filtered.filter(v => v.status.toLowerCase() === selectedStatus.value)
  }

  // Apply pagination
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage
  return filtered.slice(start, end)
})

const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage)

const totalPages = computed(() => 
  Math.ceil(vacancies.value.length / itemsPerPage)
)

const todaysJoiningSummary = computed(() => {
  return vacancies.value.reduce((sum, vacancy) => sum + (vacancy.todayJoining || 0), 0)
})

const totalVacancies = computed(() => vacancies.value.length)

const urgentVacanciesCount = computed(() => 
  vacancies.value.filter(v => 
    v.status === 'urgent' || v.status === 'very-urgent' || v.status === 'Urgent' || v.status === 'Very Urgent'
  ).length
)

// Methods
const handleSearch = () => {
  currentPage.value = 1
}

const handleFilter = () => {
  currentPage.value = 1
}

const changePage = (page: number) => {
  currentPage.value = page
}

const formatDate = (date: string) => {
  return format(new Date(date), 'dd/MM/yyyy')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[7] || (_cache[7] = [
          _createElementVNode("div", { class: "spinner" }, null, -1),
          _createElementVNode("p", null, "Loading vacancies...", -1)
        ]))]))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("p", null, _toDisplayString(error.value), 1),
            _createElementVNode("button", {
              onClick: fetchVacancies,
              class: "retry-btn"
            }, "Retry")
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _cache[9] || (_cache[9] = _createElementVNode("div", { class: "card-icon" }, [
                    _createElementVNode("i", { class: "fas fa-user-plus" })
                  ], -1)),
                  _createElementVNode("div", _hoisted_8, [
                    _cache[8] || (_cache[8] = _createElementVNode("h3", { class: "card-title" }, "Today's Joinings", -1)),
                    _createElementVNode("p", _hoisted_9, _toDisplayString(todaysJoiningSummary.value), 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _cache[11] || (_cache[11] = _createElementVNode("div", { class: "card-icon" }, [
                    _createElementVNode("i", { class: "fas fa-clipboard-list" })
                  ], -1)),
                  _createElementVNode("div", _hoisted_11, [
                    _cache[10] || (_cache[10] = _createElementVNode("h3", { class: "card-title" }, "Total Vacancies", -1)),
                    _createElementVNode("p", _hoisted_12, _toDisplayString(totalVacancies.value), 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_13, [
                  _cache[13] || (_cache[13] = _createElementVNode("div", { class: "card-icon urgent-icon" }, [
                    _createElementVNode("i", { class: "fas fa-exclamation-circle" })
                  ], -1)),
                  _createElementVNode("div", _hoisted_14, [
                    _cache[12] || (_cache[12] = _createElementVNode("h3", { class: "card-title" }, "Urgent Vacancies", -1)),
                    _createElementVNode("p", _hoisted_15, _toDisplayString(urgentVacanciesCount.value), 1)
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_16, [
                _createElementVNode("div", _hoisted_17, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                    type: "text",
                    placeholder: "Search vacancies...",
                    class: "search-input",
                    onInput: handleSearch
                  }, null, 544), [
                    [_vModelText, searchQuery.value]
                  ])
                ]),
                _createElementVNode("div", _hoisted_18, [
                  _createElementVNode("div", _hoisted_19, [
                    _withDirectives(_createElementVNode("input", {
                      type: "date",
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((fromDate).value = $event)),
                      class: "filter-date",
                      onChange: handleFilter
                    }, null, 544), [
                      [_vModelText, fromDate.value]
                    ]),
                    _cache[14] || (_cache[14] = _createElementVNode("span", { class: "date-separator" }, "to", -1)),
                    _withDirectives(_createElementVNode("input", {
                      type: "date",
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((toDate).value = $event)),
                      class: "filter-date",
                      onChange: handleFilter
                    }, null, 544), [
                      [_vModelText, toDate.value]
                    ])
                  ]),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedDepartment).value = $event)),
                    class: "filter-select",
                    onChange: handleFilter
                  }, [
                    _cache[15] || (_cache[15] = _createElementVNode("option", { value: "" }, "All Departments", -1)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(departments.value, (dept) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: dept,
                        value: dept
                      }, _toDisplayString(dept), 9, _hoisted_20))
                    }), 128))
                  ], 544), [
                    [_vModelSelect, selectedDepartment.value]
                  ]),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedStatus).value = $event)),
                    class: "filter-select",
                    onChange: handleFilter
                  }, [...(_cache[16] || (_cache[16] = [
                    _createElementVNode("option", { value: "" }, "All Status", -1),
                    _createElementVNode("option", { value: "urgent" }, "Urgent", -1),
                    _createElementVNode("option", { value: "very-urgent" }, "Very Urgent", -1)
                  ]))], 544), [
                    [_vModelSelect, selectedStatus.value]
                  ])
                ])
              ])
            ]),
            (filteredVacancies.value.length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_21, [...(_cache[17] || (_cache[17] = [
                  _createElementVNode("p", null, "No vacancies found matching your criteria", -1)
                ]))]))
              : (_openBlock(), _createElementBlock("div", _hoisted_22, [
                  _createElementVNode("table", _hoisted_23, [
                    _cache[18] || (_cache[18] = _createElementVNode("thead", null, [
                      _createElementVNode("tr", null, [
                        _createElementVNode("th", null, "SN"),
                        _createElementVNode("th", null, "Date"),
                        _createElementVNode("th", null, "Site Name"),
                        _createElementVNode("th", null, "Dept"),
                        _createElementVNode("th", null, "Post Vacant"),
                        _createElementVNode("th", null, "Reliever"),
                        _createElementVNode("th", null, "Change"),
                        _createElementVNode("th", null, "Gender"),
                        _createElementVNode("th", null, "Salary"),
                        _createElementVNode("th", null, "PF/ESI"),
                        _createElementVNode("th", null, "Stay"),
                        _createElementVNode("th", null, "W/O FF"),
                        _createElementVNode("th", null, "Food"),
                        _createElementVNode("th", null, "Location"),
                        _createElementVNode("th", null, "Hrs"),
                        _createElementVNode("th", null, "Deadline (Operation)"),
                        _createElementVNode("th", null, "Current Deadline (Recruitment)"),
                        _createElementVNode("th", null, "Missed Deadlines"),
                        _createElementVNode("th", null, "Status")
                      ])
                    ], -1)),
                    _createElementVNode("tbody", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredVacancies.value, (vacancy, index) => {
                        return (_openBlock(), _createElementBlock("tr", {
                          key: vacancy._id,
                          class: _normalizeClass({ 'urgent': vacancy.status === 'urgent', 'very-urgent': vacancy.status === 'very-urgent' })
                        }, [
                          _createElementVNode("td", null, _toDisplayString(startIndex.value + index + 1), 1),
                          _createElementVNode("td", null, _toDisplayString(formatDate(vacancy.vacancyDate)), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.siteName), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.departmentName), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.postVacant), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.relieverName), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.changeType), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.gender), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.salary), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.pfEsi ? 'Yes' : 'No'), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.stay ? 'Yes' : 'No'), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.weeklyOff), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.food), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.location), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.workingHours), 1),
                          _createElementVNode("td", null, _toDisplayString(formatDate(vacancy.operationDeadline)), 1),
                          _createElementVNode("td", null, _toDisplayString(formatDate(vacancy.recruitmentDeadline)), 1),
                          _createElementVNode("td", null, _toDisplayString(vacancy.missedDeadlines), 1),
                          _createElementVNode("td", null, [
                            _createElementVNode("span", {
                              class: _normalizeClass(['status-badge', vacancy.status])
                            }, _toDisplayString(vacancy.status), 3)
                          ])
                        ], 2))
                      }), 128))
                    ])
                  ])
                ])),
            _createElementVNode("div", _hoisted_24, [
              _createElementVNode("button", {
                disabled: currentPage.value === 1,
                onClick: _cache[5] || (_cache[5] = ($event: any) => (changePage(currentPage.value - 1))),
                class: "page-btn"
              }, " Previous ", 8, _hoisted_25),
              _createElementVNode("span", _hoisted_26, "Page " + _toDisplayString(currentPage.value) + " of " + _toDisplayString(totalPages.value), 1),
              _createElementVNode("button", {
                disabled: currentPage.value === totalPages.value,
                onClick: _cache[6] || (_cache[6] = ($event: any) => (changePage(currentPage.value + 1))),
                class: "page-btn"
              }, " Next ", 8, _hoisted_27)
            ])
          ]))
  ]))
}
}

})